import React, { useState } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import BlobStorageSummary from './BlobStorageSummary';
import ClientList from './ClientList';

const ConnectionStatus = ({ databaseStatus, onClientClick, filter, setFilter }) => {
    const [showDetails, setShowDetails] = useState({});

    const toggleDetails = (serviceName) => {
        setShowDetails(prev => ({
            ...prev,
            [serviceName]: !prev[serviceName]
        }));
    };


    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'connected':
                return 'text-green-500';
            case 'degraded':
                return 'text-yellow-500';
            case 'disconnected':
            case 'error':
                return 'text-red-500';
            default:
                return 'text-gray-500';
        }
    };

    const getStatusIcon = (status) => {
        switch (status?.toLowerCase()) {
            case 'connected':
                return 'mdi:check-circle';
            case 'degraded':
                return 'mdi:alert-circle';
            case 'disconnected':
            case 'error':
                return 'mdi:close-circle';
            default:
                return 'mdi:help-circle';
        }
    };

    const getOverallStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'healthy':
                return 'text-green-500';
            case 'degraded':
                return 'text-yellow-500';
            case 'unhealthy':
                return 'text-red-500';
            default:
                return 'text-gray-500';
        }
    };

    if (!databaseStatus) {
        return (
            <div className="card-container p-4 rounded">
                <h4 className="font-medium mb-3 text-primary">Connection Status</h4>
                <div className="text-center py-8">
                    <Icon icon="mdi:database-off" className="text-4xl text-font-secondary mb-2" />
                    <p className="text-font-secondary">No database status available</p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col xl:flex-row gap-3 w-full items-stretch">
            <div className="rounded flex flex-col gap-3 w-full h-fit">
                <h4 className="font-semibold text-font-primary flex items-center gap-2 text-base">
                    <Icon icon="pajamas:status" className="text-lg text-primary" />
                    Connection Status
                </h4>

                <hr className='border-border' />
                {/* Overall Status */}
                <div className="card-container flex flex-row items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                        <Icon 
                            icon={getStatusIcon(databaseStatus.overallStatus)} 
                            className={`text-2xl ${getOverallStatusColor(databaseStatus.overallStatus)}`} 
                        />
                        <span className="font-semibold text-font-primary">
                            Overall Status: 
                        </span>
                    </div>
                    <span className={`font-bold ${getOverallStatusColor(databaseStatus.overallStatus)}`}>
                        {databaseStatus.overallStatus}
                    </span>
                </div>

                {/* Services Status */}
                <div className="space-y-3">
                    {Object.entries(databaseStatus.services || {}).map(([serviceName, serviceData]) => (
                        <div key={serviceName} className="card-container flex flex-col items-start gap-2 w-full justify-between">
                            <div className="flex items-center justify-between w-full">
                                <div className="flex items-center gap-2 w-full">
                                    <Icon 
                                        icon={getStatusIcon(serviceData.status)} 
                                        className={`text-2xl ${getStatusColor(serviceData.status)}`} 
                                    />
                                    <div>
                                        <div className="font-semibold text-font-primary capitalize">
                                            {serviceName.replace(/([A-Z])/g, ' $1').trim()}
                                        </div>
                                        <div className="text-sm text-font-secondary">
                                            {serviceData.status} • {serviceData.responseTime}
                                        </div>
                                    </div>
                                </div>
                                
                                {serviceData.details && (
                                    <button
                                        onClick={() => toggleDetails(serviceName)}
                                        className="p-2 text-xs button-container hover:bg-bgAccent/80 rounded transition-colors"
                                    >
                                        <Icon icon="mdi:information-outline" className="text-sm" />
                                    </button>
                                )}
                            </div>
                            
                            {/* Enhanced Blob Storage Display */}
                            {serviceName === 'blobStorage' && serviceData.accounts && (
                                <div className="w-full">
                                    <BlobStorageSummary 
                                        summary={serviceData.summary}
                                        overallStatus={serviceData.status}
                                    />
                                </div>
                            )}
                            
                            {/* Details Modal */}
                            {showDetails[serviceName] && serviceData.details && (
                                <div className="w-full p-3 bg-bgAccent rounded text-xs">
                                    <div className="font-medium text-font-primary mb-2">Details:</div>
                                    <pre className="text-font-secondary whitespace-pre-wrap">
                                        {JSON.stringify(serviceData.details, null, 2)}
                                    </pre>
                                </div>
                            )}
                        </div>
                    ))}
                </div>


            </div>
            <div className="rounded flex flex-col gap-3 w-full">
                <div className="flex items-center justify-between gap-3">
                    <h5 className="font-semibold text-font-primary flex items-center gap-2 text-base">
                        <Icon icon="lucide:network" className="text-xl text-primary" />
                        Client Accounts
                    </h5>
                    <div className="flex gap-2">
                        <select
                            value={filter}
                            onChange={(e) => {
                                console.log('Filter changed to:', e.target.value);
                                setFilter(e.target.value);
                            }}
                            className="px-2 py-1 text-xs bg-bgAccent rounded border border-bgAccent focus:border-primary focus:outline-none"
                        >
                            <option value="all">All</option>
                            <option value="connected">Connected</option>
                            <option value="disconnected">Disconnected</option>
                            <option value="notConfigured">Not Configured</option>
                            <option value="issues">Issues</option>
                        </select>
                    </div>
                </div>
                
                <hr className='border-border' />
                
                <div className="flex-1 min-h-0">
                    <ClientList 
                        clients={databaseStatus.services?.blobStorage?.accounts || []}
                        onClientClick={onClientClick}
                        filter={filter}
                    />
                </div>
            </div>
        </div>
    );
};

export default ConnectionStatus;
